import React, { useState, useEffect, useRef } from 'react';
import {
    TextField,
    Box,
    Chip,
    Avatar,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    CircularProgress,
    InputAdornment,
    FormLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useDispatch, useSelector } from 'react-redux';
import { searchCompanies } from '../../redux/company/actions';
import { ICompany, ISearchCompanyResult } from '../../redux/company/types';
import { FORM_IDENTIFIER_ENUM } from '../../types/global/helper';
import { debounce } from 'lodash';
import { setSystemForm } from '../../redux/appConfigurator/action';
import { companySearchSelector } from '../../selectors/selectors';

interface CompanySearchSelectProps {
    initialValue?: ISearchCompanyResult | ISearchCompanyResult[] | ICompany | null;
    value?: ISearchCompanyResult | ISearchCompanyResult[] | ICompany | null;
    onChange: (value: ISearchCompanyResult | ISearchCompanyResult[] | null) => void;
    /** When set, shown above the field like other form labels (avoids long floating outlined label). */
    label?: string;
    placeholder?: string;
    multiple?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    sx?: any;
}

const CompanySearchSelect: React.FC<CompanySearchSelectProps> = ({
    value,
    onChange,
    label,
    placeholder = 'Search companies...',
    multiple = false,
    disabled = false,
    error = false,
    helperText,
    sx,
}) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();

    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const { searchCompaniesData, searchingCompanies } = useSelector(companySearchSelector);

    const debouncedSearch = useRef(
        debounce((term: string) => {
            if (term.trim().length > 2) {
                dispatch(searchCompanies({ searchTerm: term.trim() }));
            }

            if (term.length === 0) {
                dispatch(searchCompanies({ searchTerm: '' }));
            }
        }, 500),
    ).current;

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    // Handle input focus
    const handleInputFocus = () => {
        setIsOpen(true);
        if (searchTerm.trim().length >= 2) {
            dispatch(searchCompanies({ searchTerm: searchTerm.trim() }));
        }
    };

    // Handle input blur
    const handleInputBlur = () => {
        // Delay closing to allow for clicks on dropdown items
        setTimeout(() => setIsOpen(false), 200);
    };

    // Handle input change
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = event.target.value;
        setInputValue(newValue);
        debouncedSearch(newValue);

        if (!newValue) {
            setIsOpen(false);
        } else {
            setIsOpen(true);
        }
    };

    // Handle company selection
    const handleCompanySelect = (company: ISearchCompanyResult) => {
        if (multiple) {
            const currentValue = Array.isArray(value) ? value : [];
            const isAlreadySelected = currentValue.some(item => item._id === company._id);

            if (isAlreadySelected) {
                const newValue = currentValue.filter(item => item._id !== company._id);
                onChange(newValue.length > 0 ? newValue : null);
            } else {
                onChange([...currentValue, company]);
            }
        } else {
            onChange(company);
            setIsOpen(false);
        }

        setInputValue('');
        setSearchTerm('');
    };

    // Handle chip removal
    const handleChipDelete = (companyId: string) => {
        if (multiple && Array.isArray(value)) {
            const newValue = value.filter(item => item._id !== companyId);
            onChange(newValue.length > 0 ? newValue : null);
        } else {
            onChange(null);
        }
    };

    // Handle create new company
    const handleCreateNewCompany = () => {
        dispatch(setSystemForm(FORM_IDENTIFIER_ENUM.CREATE_PRODUCTION_COMPANY));
        setIsOpen(false);
        setInputValue('');
        setSearchTerm('');
    };

    // Get search results
    const searchResults = searchCompaniesData;

    // Check if company is selected
    const isCompanySelected = (company: ISearchCompanyResult): boolean => {
        if (multiple && Array.isArray(value)) {
            return value.some(item => item._id === company._id);
        }
        return !!(value && !Array.isArray(value) && value._id === company._id);
    };

    // Render chips for input adornment
    const renderInputChips = () => {
        if (!value) return null;

        if (multiple && Array.isArray(value)) {
            if (value.length === 0) return null;

            // Show first item and count of others
            const firstItem = value[0];
            const remainingCount = value.length - 1;

            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        maxWidth: 'calc(100% - 40px)',
                    }}>
                    <Chip
                        avatar={
                            <Avatar
                                src={firstItem.profile_picture}
                                alt={firstItem.company_name}
                                sx={{ width: 20, height: 20 }}
                            />
                        }
                        label={firstItem.company_name}
                        onDelete={() => handleChipDelete(firstItem._id)}
                        deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                        size="small"
                        sx={{
                            backgroundColor: theme[themeStyle].form_black,
                            color: theme[themeStyle].tag_white,
                            height: 24,
                            '& .MuiChip-label': {
                                fontSize: '0.75rem',
                                px: 1,
                            },
                            '& .MuiChip-deleteIcon': {
                                color: theme[themeStyle].tag_white,
                                fontSize: 16,
                                '&:hover': {
                                    color: theme[themeStyle].red_status_indicator,
                                },
                            },
                        }}
                    />
                    {remainingCount > 0 && (
                        <Chip
                            label={`+${remainingCount} more`}
                            size="small"
                            sx={{
                                backgroundColor: theme[themeStyle].highlight,
                                color: theme[themeStyle].black,
                                height: 24,
                                '& .MuiChip-label': {
                                    fontSize: '0.75rem',
                                    px: 1,
                                },
                            }}
                        />
                    )}
                </Box>
            );
        } else if (!Array.isArray(value)) {
            return (
                <Chip
                    avatar={
                        <Avatar src={value.profile_picture} alt={value.company_name} sx={{ width: 20, height: 20 }} />
                    }
                    label={value.company_name}
                    onDelete={() => handleChipDelete(value._id)}
                    deleteIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                    size="small"
                    sx={{
                        backgroundColor: theme[themeStyle].deep_grey,
                        color: theme[themeStyle].tag_white,
                        height: 24,
                        '& .MuiChip-label': {
                            fontSize: '0.75rem',
                            px: 1,
                        },
                        '& .MuiChip-deleteIcon': {
                            color: theme[themeStyle].tag_white,
                            fontSize: 16,
                            '&:hover': {
                                color: theme[themeStyle].red_status_indicator,
                            },
                        },
                        mr: 1,
                    }}
                />
            );
        }
        return null;
    };

    const useExternalLabel = label != null && label !== '';

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {useExternalLabel && (
                <FormLabel
                    sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        fontSize: '0.95rem',
                        color: 'rgba(255,255,255,0.65)',
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        mb: 0.5,
                        lineHeight: 1.5,
                    }}>
                    {label}
                </FormLabel>
            )}
            {/* Input Field */}
            <TextField
                ref={inputRef}
                fullWidth
                value={inputValue}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                placeholder={placeholder}
                label={useExternalLabel ? undefined : placeholder}
                disabled={disabled}
                error={error}
                helperText={helperText}
                InputProps={{
                    startAdornment: renderInputChips(),
                    endAdornment: (
                        <InputAdornment position="end">
                            {searchingCompanies && (
                                <CircularProgress size={20} sx={{ color: theme[themeStyle].highlight }} />
                            )}
                        </InputAdornment>
                    ),
                }}
                sx={[
                    {
                        '& .MuiOutlinedInput-root': {
                            color: theme[themeStyle].tag_white,
                            backgroundColor: theme[themeStyle].gray_text,
                            minHeight: value ? 'auto' : '56px',
                            '& fieldset': {
                                border: error ? `1px solid ${theme[themeStyle].red_status_indicator}` : 'none',
                            },
                            '&:hover fieldset': {
                                border: 'none',
                            },
                            '&.Mui-focused fieldset': {
                                border: 'none',
                            },
                            '&.Mui-focused': {
                                boxShadow: `0 0 0 1px ${theme[themeStyle].highlight}`,
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: theme[themeStyle].tag_white,
                            '&.Mui-focused': {
                                color: theme[themeStyle].highlight,
                            },
                        },
                        '& .MuiFormHelperText-root': {
                            color: error ? theme[themeStyle].red_status_indicator : theme[themeStyle].tag_white,
                            opacity: 0.7,
                        },
                    },
                    ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
                ]}
            />

            {/* Dropdown */}
            {isOpen && (searchTerm.trim().length >= 0 || (searchResults && searchResults?.length > 0)) && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        backgroundColor: theme[themeStyle].gray_text,
                        border: `1px solid ${theme[themeStyle].form_gray}`,
                        borderRadius: '8px',
                        maxHeight: 300,
                        overflow: 'auto',
                        mt: 1,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                    }}>
                    <List sx={{ p: 0 }}>
                        {/* Search Results */}
                        {searchResults?.map((company: ISearchCompanyResult) => (
                            <ListItem
                                key={company._id}
                                disablePadding
                                sx={{
                                    backgroundColor: isCompanySelected(company)
                                        ? theme[themeStyle].form_divider
                                        : 'transparent',
                                }}>
                                <ListItemButton
                                    onClick={() => handleCompanySelect(company)}
                                    disabled={isCompanySelected(company)}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: theme[themeStyle].form_divider,
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: theme[themeStyle].form_divider,
                                            opacity: 0.6,
                                        },
                                    }}>
                                    <ListItemAvatar>
                                        <Avatar
                                            src={company.profile_picture}
                                            alt={company.company_name}
                                            sx={{ width: 40, height: 40 }}
                                        />
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: '0.875rem',
                                                }}>
                                                {company.company_name}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].darkGray60,
                                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                    fontSize: '0.75rem',
                                                }}>
                                                {new Date(company.date_of_formation).getFullYear()}
                                            </Typography>
                                        }
                                    />
                                    {isCompanySelected(company) && (
                                        <IconButton size="small" disabled>
                                            <AddIcon
                                                sx={{
                                                    color: theme[themeStyle].highlight,
                                                    fontSize: 20,
                                                }}
                                            />
                                        </IconButton>
                                    )}
                                </ListItemButton>
                            </ListItem>
                        ))}

                        {/* Create New Company Option - Always show */}
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={handleCreateNewCompany}
                                sx={{
                                    '&:hover': {
                                        backgroundColor: theme[themeStyle].form_divider,
                                    },
                                }}>
                                <ListItemAvatar>
                                    <Avatar
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            backgroundColor: theme[themeStyle].highlight,
                                        }}>
                                        <AddIcon sx={{ color: theme[themeStyle].black }} />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].highlight,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                fontSize: '0.875rem',
                                            }}>
                                            Create new company
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].darkGray60,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.75rem',
                                            }}>
                                            {searchTerm.trim().length >= 2
                                                ? `"${searchTerm}" not found? Create it`
                                                : 'Add a new company to the platform'}
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        </ListItem>

                        {/* No Results */}
                        {searchTerm.trim().length >= 2 && searchResults?.length === 0 && !searchingCompanies && (
                            <ListItem>
                                <ListItemText
                                    primary={
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].darkGray60,
                                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                                                fontSize: '0.875rem',
                                                textAlign: 'center',
                                            }}>
                                            No companies found
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        )}
                    </List>
                </Box>
            )}
        </Box>
    );
};

export { CompanySearchSelect };
